/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { carrierByActiveByName } from "../../../../graphql/queries";

import { CircularProgress, InputAdornment } from "@material-ui/core";

import { AutocompleteInput, useNotify } from "react-admin";
import { useCreateSuggestionContext } from "ra-ui-materialui/esm/input/useSupportCreateSuggestion";

import { logError } from "../../../../utils/appUtils";
import { CarrierCreateDialog } from "../CarrierCreateDialog";

const getCarriers = async (
  setCarriers,
  notify,
  setFetching,
  onCarriersLoaded,
) => {
  try {
    const response = await API.graphql(
      graphqlOperation(carrierByActiveByName, {
        reloadYardId: window.appUser?.reloadYardId,
        activeBusinessName: { beginsWith: { active: "true" } },
        limit: 1000,
      }),
    );
    const items = response.data.carrierByActiveByName.items;
    setCarriers(items);
    onCarriersLoaded?.(items);
    setFetching(false);
  } catch (err) {
    logError("common:CarrierDropDown:getCarriers", err, notify);
    setFetching(false);
  }
};

// Fallback to record.name handles RA's synthetic create-suggestion item,
// which has { id: 'RA_CREATE', name: createItemLabel } but no businessName.
const carrierNameRenderer = (record) => {
  if (!record) return "";
  return record.businessName || record.name || "";
};

// Rendered by RA when the user clicks "Create new carrier" (enableCreate mode).
// Always opens the dialog — no free-text path.
const CarrierDialogAdapter = ({
  carriers,
  setCarriers,
  pendingCarrierRef,
  blurInput,
}) => {
  const { onCreate, onCancel } = useCreateSuggestionContext();
  return (
    <CarrierCreateDialog
      open={true}
      onClose={onCancel}
      carriers={carriers}
      setCarriers={setCarriers}
      setSelectedCarrier={() => {}}
      onSuccess={(newCarrier) => {
        pendingCarrierRef.current = newCarrier;
        onCreate({ id: newCarrier.id, businessName: newCarrier.businessName });
        blurInput();
      }}
    />
  );
};

// Rendered by RA when the user clicks 'Use "%{item}"' (enableFreeText mode).
// Non-empty filter → accept typed text directly (no dialog).
// Empty filter    → open CarrierCreateDialog to persist a real carrier.
const CarrierFreeTextAdapter = ({
  carriers,
  setCarriers,
  onFreeTextAccepted,
  onCarrierCreated,
}) => {
  const { filter, onCreate, onCancel } = useCreateSuggestionContext();

  useEffect(() => {
    if (filter) {
      onFreeTextAccepted(filter, onCreate);
    }
  }, []);

  if (filter) return null;

  return (
    <CarrierCreateDialog
      open={true}
      onClose={onCancel}
      carriers={carriers}
      setCarriers={setCarriers}
      setSelectedCarrier={() => {}}
      onSuccess={(newCarrier) => onCarrierCreated(newCarrier, onCreate)}
    />
  );
};

export const CarrierDropDown = ({
  source,
  label,
  onCarrierChange,
  helperText,
  validate,
  allowEmpty = true,
  alwaysOn = true,
  loading = false,
  onCarriersLoaded,
  enableCreate = false, // "Create new carrier" → opens dialog, stores carrier id
  enableFreeText = false, // 'Use "%{item}"' → typed text or dialog, stores businessName
  ...props
}) => {
  const notify = useNotify();
  const [carriers, setCarriers] = useState([]);
  const [freeTextChoices, setFreeTextChoices] = useState([]);
  const [fetching, setFetching] = useState(true);
  const isLoading = loading || fetching;
  const pendingCarrierRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    getCarriers(setCarriers, notify, setFetching, onCarriersLoaded);
  }, []);

  const blurInput = () => {
    setTimeout(() => {
      containerRef.current?.querySelector("input")?.blur();
    }, 0);
  };

  const handleFreeTextAccepted = (filter, raOnCreate) => {
    const newItem = { id: filter, businessName: filter };
    pendingCarrierRef.current = newItem;
    setFreeTextChoices((prev) => [
      ...prev.filter((c) => c.businessName !== filter),
      newItem,
    ]);
    raOnCreate(newItem);
  };

  const handleCarrierCreated = (newCarrier, raOnCreate) => {
    pendingCarrierRef.current = newCarrier;
    raOnCreate({
      id: newCarrier.businessName,
      businessName: newCarrier.businessName,
    });
    blurInput();
  };

  const handleChange = (value) => {
    if (!onCarrierChange) return;
    if (!value) {
      onCarrierChange(null);
      return;
    }
    if (enableFreeText) {
      if (pendingCarrierRef.current?.businessName === value) {
        onCarrierChange(pendingCarrierRef.current);
        pendingCarrierRef.current = null;
        return;
      }
      onCarrierChange(carriers.find((c) => c.businessName === value) ?? null);
    } else {
      if (pendingCarrierRef.current?.id === value) {
        onCarrierChange(pendingCarrierRef.current);
        pendingCarrierRef.current = null;
        return;
      }
      onCarrierChange(carriers.find((c) => c.id === value) ?? null);
    }
  };

  const hasCreate = enableCreate || enableFreeText;
  const createElement = hasCreate ? (
    enableFreeText ? (
      <CarrierFreeTextAdapter
        carriers={carriers}
        setCarriers={setCarriers}
        onFreeTextAccepted={handleFreeTextAccepted}
        onCarrierCreated={handleCarrierCreated}
      />
    ) : (
      <CarrierDialogAdapter
        carriers={carriers}
        setCarriers={setCarriers}
        pendingCarrierRef={pendingCarrierRef}
        blurInput={blurInput}
      />
    )
  ) : undefined;

  return (
    <div ref={containerRef}>
      <AutocompleteInput
        {...props}
        source={source}
        label={label}
        variant="outlined"
        fullWidth
        allowEmpty={allowEmpty}
        resettable={!isLoading}
        clearable
        validate={validate}
        choices={enableFreeText ? [...carriers, ...freeTextChoices] : carriers}
        optionText={carrierNameRenderer}
        optionValue={enableFreeText ? "businessName" : "id"}
        onChange={handleChange}
        loading={fetching}
        loaded={!fetching}
        helperText={helperText}
        create={createElement}
        createLabel={hasCreate ? "Create new carrier" : undefined}
        createItemLabel={
          enableFreeText
            ? 'Use "%{item}"'
            : enableCreate
              ? "Create new carrier"
              : undefined
        }
        options={{
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                {isLoading && <CircularProgress size={20} color="inherit" />}
              </InputAdornment>
            ),
          },
        }}
      />
    </div>
  );
};
